import React, { useState } from 'react';
import CampoTexto from '@/components/Formulario/CampoTexto';
import CampoNumero from '@/components/Formulario/CampoNumero';
import CampoSeletor from '@/components/Formulario/CampoSeletor';
import CampoCheckbox from '@/components/Formulario/CampoCheckbox';
import CampoMultiTexto from '@/components/Formulario/CampoMultiTexto';
import Grid, { GridItem } from '@/components/layout/Grid';
import VBox from '@/components/layout/VBox';
import HBox from '@/components/layout/HBox';
import Botao from '@/components/Botao';
import Icon from '@mdi/react';
import { mdiPlus, mdiTrashCan } from '@mdi/js';
import { DnsCacheConfigDTO, DnsStaticEntryDTO, DnsStaticEntryType } from '@webip/shared/src/dto/appliance';

type Props = {
    value: Partial<DnsCacheConfigDTO>;
    onChange: (v: Partial<DnsCacheConfigDTO>) => void;
    fieldErrors?: Record<string, string>;
};

const tipoOptions: { id: DnsStaticEntryType; label: string }[] = [
    { id: 'A', label: 'A (IPv4)' },
    { id: 'AAAA', label: 'AAAA (IPv6)' },
    { id: 'CNAME', label: 'CNAME' },
    { id: 'MX', label: 'MX (Mail)' },
    { id: 'SRV', label: 'SRV' },
    { id: 'TXT', label: 'TXT' },
    { id: 'NXDOMAIN', label: 'NXDOMAIN' },
    { id: 'NS', label: 'NS' },
    { id: 'FWD', label: 'FWD (Forward)' },
];

const newEntry = (applianceId: number): DnsStaticEntryDTO => ({
    appliance_id: applianceId,
    nome: '',
    type: 'A',
    disabled: false,
});

const EntradaRow = ({
    entry,
    idx,
    onChange,
    onRemove,
}: {
    entry: DnsStaticEntryDTO;
    idx: number;
    onChange: (e: DnsStaticEntryDTO) => void;
    onRemove: () => void;
}) => {
    const update = (patch: Partial<DnsStaticEntryDTO>) => onChange({ ...entry, ...patch });
    const tipo = entry.type;

    return (
        <div style={{ border: '1px solid var(--tc-color-gray-200)', borderRadius: 8, padding: '12px', background: 'var(--tc-color-gray-50)' }}>
            <HBox style={{ gap: 8, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                {/* Nome */}
                <div style={{ flex: '2 1 160px', minWidth: 120 }}>
                    <CampoTexto
                        label={idx === 0 ? 'Nome (hostname)' : undefined}
                        value={entry.nome}
                        onChange={(v) => update({ nome: v })}
                        placeholder="ex: mail.empresa.com"
                    />
                </div>

                {/* Tipo */}
                <div style={{ flex: '1 1 130px', minWidth: 110 }}>
                    <CampoSeletor
                        label={idx === 0 ? 'Tipo' : undefined}
                        value={entry.type}
                        options={tipoOptions}
                        onChange={(v) => update({ type: v as DnsStaticEntryType })}
                    />
                </div>

                {/* Valor dinâmico por tipo */}
                {(tipo === 'A' || tipo === 'AAAA') && (
                    <div style={{ flex: '2 1 160px', minWidth: 120 }}>
                        <CampoTexto
                            label={idx === 0 ? 'Endereço IP' : undefined}
                            value={entry.address ?? ''}
                            onChange={(v) => update({ address: v })}
                            placeholder={tipo === 'A' ? '192.168.1.10' : '::1'}
                        />
                    </div>
                )}
                {tipo === 'CNAME' && (
                    <div style={{ flex: '2 1 160px', minWidth: 120 }}>
                        <CampoTexto
                            label={idx === 0 ? 'Destino (CNAME)' : undefined}
                            value={entry.cname ?? ''}
                            onChange={(v) => update({ cname: v })}
                            placeholder="alias.empresa.com"
                        />
                    </div>
                )}
                {tipo === 'MX' && (
                    <>
                        <div style={{ flex: '2 1 160px', minWidth: 120 }}>
                            <CampoTexto
                                label={idx === 0 ? 'Servidor MX' : undefined}
                                value={entry.exchange ?? ''}
                                onChange={(v) => update({ exchange: v })}
                                placeholder="mail.empresa.com"
                            />
                        </div>
                        <div style={{ flex: '0 0 80px' }}>
                            <CampoNumero
                                label={idx === 0 ? 'Pref.' : undefined}
                                value={entry.mx_preference ?? 10}
                                onChange={(v) => update({ mx_preference: v })}
                            />
                        </div>
                    </>
                )}
                {tipo === 'SRV' && (
                    <>
                        <div style={{ flex: '2 1 140px', minWidth: 100 }}>
                            <CampoTexto
                                label={idx === 0 ? 'Target' : undefined}
                                value={entry.srv_target ?? ''}
                                onChange={(v) => update({ srv_target: v })}
                                placeholder="host.empresa.com"
                            />
                        </div>
                        <div style={{ flex: '0 0 70px' }}>
                            <CampoNumero
                                label={idx === 0 ? 'Porta' : undefined}
                                value={entry.srv_port ?? 0}
                                onChange={(v) => update({ srv_port: v })}
                            />
                        </div>
                        <div style={{ flex: '0 0 60px' }}>
                            <CampoNumero
                                label={idx === 0 ? 'Prior.' : undefined}
                                value={entry.srv_priority ?? 0}
                                onChange={(v) => update({ srv_priority: v })}
                            />
                        </div>
                        <div style={{ flex: '0 0 60px' }}>
                            <CampoNumero
                                label={idx === 0 ? 'Peso' : undefined}
                                value={entry.srv_weight ?? 0}
                                onChange={(v) => update({ srv_weight: v })}
                            />
                        </div>
                    </>
                )}
                {tipo === 'TXT' && (
                    <div style={{ flex: '3 1 200px', minWidth: 140 }}>
                        <CampoTexto
                            label={idx === 0 ? 'Conteúdo TXT' : undefined}
                            value={entry.text ?? ''}
                            onChange={(v) => update({ text: v })}
                            placeholder={'"v=spf1 include:... ~all"'}
                        />
                    </div>
                )}
                {(tipo === 'NS' || tipo === 'FWD') && (
                    <div style={{ flex: '2 1 160px', minWidth: 120 }}>
                        <CampoTexto
                            label={idx === 0 ? (tipo === 'NS' ? 'Servidor NS' : 'Reencaminhar para') : undefined}
                            value={entry.exchange ?? ''}
                            onChange={(v) => update({ exchange: v })}
                            placeholder="ns1.empresa.com"
                        />
                    </div>
                )}
                {tipo === 'NXDOMAIN' && (
                    <div style={{ flex: '2 1 160px', minWidth: 120, alignSelf: 'center', color: 'var(--tc-color-gray-500)', fontSize: 12 }}>
                        {idx === 0 && <div style={{ marginBottom: 4 }}>&nbsp;</div>}
                        Bloqueia resolução para este nome
                    </div>
                )}

                {/* TTL */}
                <div style={{ flex: '0 0 80px' }}>
                    <CampoTexto
                        label={idx === 0 ? 'TTL' : undefined}
                        value={entry.ttl ?? ''}
                        onChange={(v) => update({ ttl: v })}
                        placeholder="1h"
                    />
                </div>

                {/* Disabled + Remover */}
                <div style={{ flex: '0 0 auto', display: 'flex', alignItems: 'flex-end', gap: 8, paddingBottom: 4 }}>
                    {idx === 0 && <div style={{ marginBottom: 28 }} />}
                    <CampoCheckbox
                        text="Desabilitado"
                        value={entry.disabled ?? false}
                        onChange={(v) => update({ disabled: v })}
                    />
                    <Botao
                        size="sm"
                        variant="outline-danger"
                        icon={mdiTrashCan}
                        onClick={onRemove}
                        title="Remover entrada"
                    />
                </div>
            </HBox>
        </div>
    );
};

const FormDnsCache = ({ value, onChange, fieldErrors = {} }: Props) => {
    const update = (patch: Partial<DnsCacheConfigDTO>) => onChange({ ...value, ...patch });

    const entradas = value.entradas_estaticas ?? [];
    const applianceId = value.appliance_id ?? 0;

    const handleAddEntrada = () => {
        update({ entradas_estaticas: [...entradas, newEntry(applianceId)] });
    };

    const handleChangeEntrada = (idx: number, e: DnsStaticEntryDTO) => {
        const next = [...entradas];
        next[idx] = e;
        update({ entradas_estaticas: next });
    };

    const handleRemoveEntrada = (idx: number) => {
        update({ entradas_estaticas: entradas.filter((_, i) => i !== idx) });
    };

    return (
        <VBox style={{ gap: 20 }}>
            {/* Configuração geral */}
            <Grid>
                <GridItem sm={12}>
                    <CampoCheckbox
                        label="Cache DNS"
                        text="Habilitar cache DNS (allow-remote-requests)"
                        value={value.ativo ?? false}
                        onChange={(v) => update({ ativo: v })}
                    />
                </GridItem>
                <GridItem sm={12}>
                    <CampoMultiTexto
                        label="Servidores DNS upstream"
                        value={value.servidores ?? []}
                        onChange={(v) => update({ servidores: v })}
                        placeholder="1.1.1.1"
                    />
                </GridItem>
            </Grid>

            {/* Restrições de bypass de DNS */}
            <VBox style={{ gap: 8 }}>
                <span style={{ fontWeight: 600, fontSize: 14 }}>Restrições de bypass</span>
                <Grid>
                    <GridItem sm={12}>
                        <CampoCheckbox
                            text="Capturar solicitações DNS"
                            hint="Redireciona (dst-nat) toda consulta DNS 53/tcp+udp da LAN para o resolver do próprio roteador, mesmo que o dispositivo esteja configurado com outro DNS."
                            value={value.capturar_dns ?? false}
                            onChange={(v) => update({ capturar_dns: v })}
                        />
                    </GridItem>
                    <GridItem sm={12}>
                        <CampoCheckbox
                            text="Restringir DoH (DNS over HTTPS)"
                            hint='Adiciona a entrada estática "use-application-dns.net" como NXDOMAIN — sinal que navegadores como Chrome e Firefox usam para desabilitar o DoH automaticamente na rede.'
                            value={value.restringir_doh ?? false}
                            onChange={(v) => update({ restringir_doh: v })}
                        />
                    </GridItem>
                    <GridItem sm={12}>
                        <CampoCheckbox
                            text="Restringir DoT (DNS over TLS)"
                            hint="Bloqueia a porta 853 (tcp/udp) no roteamento, impedindo consultas DNS criptografadas para resolvers externos."
                            value={value.restringir_dot ?? false}
                            onChange={(v) => update({ restringir_dot: v })}
                        />
                    </GridItem>
                    <GridItem sm={12}>
                        <CampoCheckbox
                            text="Restringir QUIC/HTTP3"
                            hint="Bloqueia UDP nas portas 80 e 443, forçando o navegador a usar HTTP/HTTPS sobre TCP — necessário para que o bloqueio por SNI/domínio funcione."
                            value={value.restringir_quic ?? false}
                            onChange={(v) => update({ restringir_quic: v })}
                        />
                    </GridItem>
                </Grid>
            </VBox>

            {/* Entradas estáticas */}
            <VBox style={{ gap: 8 }}>
                <HBox style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontWeight: 600, fontSize: 14 }}>
                        Entradas estáticas ({entradas.length})
                    </span>
                    <Botao
                        size="sm"
                        variant="outline-primary"
                        icon={mdiPlus}
                        onClick={handleAddEntrada}
                    >
                        Adicionar entrada
                    </Botao>
                </HBox>

                {entradas.length === 0 && (
                    <div style={{ padding: '16px', textAlign: 'center', color: 'var(--tc-color-gray-400)', fontSize: 13, border: '1px dashed var(--tc-color-gray-200)', borderRadius: 8 }}>
                        Nenhuma entrada estática configurada.
                    </div>
                )}

                {entradas.map((e, idx) => (
                    <EntradaRow
                        key={idx}
                        entry={e}
                        idx={idx}
                        onChange={(updated) => handleChangeEntrada(idx, updated)}
                        onRemove={() => handleRemoveEntrada(idx)}
                    />
                ))}
            </VBox>
        </VBox>
    );
};

export default FormDnsCache;
